import { isAxiosError } from "axios";
import { ErrorResponse, ErrorResponseWithSeverity } from "../types/formErrorTypes";
import { FormValidationErrors } from "../types/form1094c";

type ValidationErrorResponse =
  | ErrorResponse
  | ErrorResponseWithSeverity
  | FormValidationErrors;

const getObjectErrorDescriptions = (errors: FormValidationErrors["errors"]) =>
  Object.entries(errors).flatMap(([field, messages]) =>
    messages.map((message) => `${field}: ${message}`)
  );

export function getErrorDescriptions(error: Error): string[] {
  if (!isAxiosError(error)) {
    return [error.message || "An unexpected error occurred"];
  }

  const errorResponse = error.response?.data as ValidationErrorResponse | undefined;

  if (errorResponse?.errors && !Array.isArray(errorResponse.errors)) {
    return getObjectErrorDescriptions(errorResponse.errors);
  }

  if (Array.isArray(errorResponse?.errors)) {
    return errorResponse.errors
      .map((validationError) => validationError.description)
      .filter(Boolean);
  }

  if (errorResponse && "detail" in errorResponse && errorResponse.detail) {
    return [errorResponse.detail];
  }

  if (errorResponse?.title) {
    return [errorResponse.title];
  }

  return [error.message || "An unexpected error occurred"];
}

export const isValidationErrorResponse = (data: unknown): boolean => {
  if (!data || typeof data !== "object") {
    return false;
  }

  const candidate = data as Partial<ValidationErrorResponse>;
  return (
    Array.isArray(candidate.errors) ||
    ("detail" in candidate && !!candidate.detail) ||
    !!candidate.title
  );
};

export const isErrorResponseWithSeverity = (
  data: unknown
): data is ErrorResponseWithSeverity => {
  if (!data || typeof data !== "object") {
    return false;
  }

  const candidate = data as Partial<ErrorResponseWithSeverity>;

  if (!Array.isArray(candidate.errors)) {
    return false;
  }

  return candidate.errors.every(
    (error) =>
      !!error &&
      typeof error === "object" &&
      typeof error.severity === "number"
  );
};

export default function handleError(
  error: Error,
  setServerErrors: (errors: string[]) => void
) {
  const descriptions = getErrorDescriptions(error);
  setServerErrors(descriptions);
}
